import { useCallback, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import {
  getBusinessFilesThunk,
  selectCurrentBusiness,
  selectCurrentBusinessFiles,
} from "../../businessesSlice";
import { nextBusinessFilesRefreshMs } from "./businessFiles.helpers";

export const FILES_REFRESH_BUFFER_MS = 30_000;

/**
 * Loads the Files list while a Files screen is on screen and re-signs its links
 * before they expire. Idle while hidden, so a mounted-but-closed screen signs nothing.
 */
export function useBusinessFilesRefresh(active: boolean): void {
  const dispatch = useAppDispatch();
  const business = useAppSelector(selectCurrentBusiness);
  const files = useAppSelector(selectCurrentBusinessFiles);
  const loadedBusinessId = useRef<string>();

  const fetchFiles = useCallback(
    (silent: boolean) => {
      if (!business?._id || !business.rootUser?.email) return;
      loadedBusinessId.current = business._id;
      dispatch(
        getBusinessFilesThunk({
          email: business.rootUser.email,
          businessRegistrationNumber: business.businessRegistrationNumber,
          businessName: business.businessName,
          iban: business.bankAccountIban || "",
          name: business.rootUser.name,
          silent,
        })
      );
    },
    [business, dispatch]
  );

  // A new business shows the loader; reopening the same one re-signs in place.
  useEffect(() => {
    if (!active) return;
    fetchFiles(loadedBusinessId.current === business?._id);
  }, [active, business?._id, fetchFiles]);

  useEffect(() => {
    if (!active) return;
    const delayMs = nextBusinessFilesRefreshMs(
      files ?? [],
      FILES_REFRESH_BUFFER_MS
    );
    if (delayMs === undefined) return;
    const timer = setTimeout(() => fetchFiles(true), delayMs);
    return () => clearTimeout(timer);
  }, [active, files, fetchFiles]);
}
